import { ChevronRight, ChevronLeft, Loader2, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { StepProps } from "../types";
import { QualifyingQuestion as QualifyingQuestionType } from "@/types/jobAdsTypes";
import { QualifyingQuestion } from "./QualifyingQuestion";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

interface StepFourProps extends StepProps {
  qualifyingQuestions: QualifyingQuestionType[];
  onQuestionsChange: (questions: QualifyingQuestionType[]) => void;
  isGeneratingQuestions: boolean;
  questionSourceDocuments?: string[];
  onRegenerateQuestions: () => void;
  onBack?: () => void;
  onForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
}

export const StepFour = ({
  onNext,
  qualifyingQuestions,
  onQuestionsChange,
  isGeneratingQuestions,
  questionSourceDocuments = [],
  onRegenerateQuestions,
  onBack,
  onForward,
  canGoBack = true,
  canGoForward = false,
}: StepFourProps) => {
  const { t } = useTranslation("recruit");
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = parseInt(active.id);
      const newIndex = parseInt(over.id);

      const newQuestions = arrayMove(qualifyingQuestions, oldIndex, newIndex).map(
        (q, index) => ({
          ...q,
          order: index + 1,
        })
      );

      onQuestionsChange(newQuestions);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center text-white text-sm font-medium">
          4
        </div>
        <h2 className="text-xl font-semibold mb-0">
          {t("ads.createModal.step4.title")}
        </h2>
      </div>

      <p className="text-muted-foreground">
        {t("ads.createModal.step4.description")}
      </p>

      {/* Which of the company's own documents shaped the questions. Named
          documents make a specific requirement checkable; their absence tells
          the recruiter the questions rest on the ad text alone. */}
      {qualifyingQuestions.length > 0 && (
        <p
          className="text-sm text-muted-foreground flex items-start gap-1.5"
          data-testid="questions-ai-grounding"
        >
          <FileText className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            {questionSourceDocuments.length > 0
              ? t("ads.createModal.step4.groundedIn", {
                  documents: questionSourceDocuments.join(", "),
                })
              : t("ads.createModal.step4.notGrounded")}
          </span>
        </p>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">{t("ads.createModal.step4.questions")}</h4>
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerateQuestions}
            disabled={isGeneratingQuestions}
          >
            {isGeneratingQuestions ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("ads.createModal.step4.generateNew")
            )}
          </Button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={qualifyingQuestions.map((_, i) => i.toString())}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {qualifyingQuestions.map((question, index) => (
                <QualifyingQuestion
                  key={index}
                  question={question}
                  index={index}
                  onQuestionChange={(updatedQuestion) => {
                    const newQuestions = [...qualifyingQuestions];
                    newQuestions[index] = updatedQuestion;
                    onQuestionsChange(newQuestions);
                  }}
                  onDelete={() => {
                    onQuestionsChange(
                      qualifyingQuestions
                        .filter((_, i) => i !== index)
                        .map((q, i) => ({ ...q, order: i + 1 }))
                    );
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <Button
          variant="outline"
          onClick={() => {
            onQuestionsChange([
              ...qualifyingQuestions,
              {
                question: "Har du erfarenhet av...?",
                positiveResponse: "Ja",
                negativeResponse: "Nej",
                onPositiveResponse: "Perfekt! Vi söker just denna kompetens.",
                onNegativeResponse: "Tyvärr är denna erfarenhet ett krav för tjänsten.",
                isRequired: true,
                order: qualifyingQuestions.length + 1,
              },
            ]);
          }}
        >
          Lägg till fråga
        </Button>
      </div>

      <div className="flex justify-between items-center pt-4 border-t">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={!canGoBack}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Föregående
        </Button>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onForward}
            disabled={!canGoForward}
            className="flex items-center gap-2"
          >
            Nästa
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          <Button onClick={onNext} className="flex items-center gap-2">
            Förhandsgranska
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepFour; 